import Icon from "@expo/vector-icons/MaterialCommunityIcons";
import { useAppStore } from "@hooks/useAppStore";
import { useAppTheme } from "@hooks/useAppTheme";
import { Box, MarkdownRenderer, TooltipWrapper, useDevice } from "@memorang/ui";
import CustomMarkdownRenderer from "components/CustomMarkdownRenderer";
import { Image } from "expo-image";
import { useState } from "react";
import { Pressable, ScrollView } from "react-native";
import { Text } from "react-native-paper";

interface ModuleLandingData {
	module?: {
		label?: string;
		title?: string;
		badgeSource?: string | number | { uri: string };
		durationLabel?: string;
		activityCount?: number;
		knowledgeCheckQuestionCount?: number;
	};
	lesson: { heading: string; text: string };
	outline: {
		heading: string;
		sections: { title: string; items: OutlineItem[] }[];
	};
	learningObjectives: { heading: string; intro: string; items: string[] };
}

interface Props {
	data: ModuleLandingData;
}

type IconName = keyof typeof Icon.glyphMap;

// Outline items arrive as plain labels (legacy modules) or objects carrying
// a data-driven icon.
type OutlineItem = string | { title: string; icon?: string };
type OutlineActivity = { title: string; icon?: string };

const toOutlineActivity = (item: OutlineItem): OutlineActivity =>
	typeof item === "string" ? { title: item } : item;

const activityIconByLabel: Record<string, IconName> = {
	Video: "play-circle-outline",
	Perspectives: "forum-outline",
	Reflect: "note-edit-outline",
	Explore: "compass-outline",
	Review: "clipboard-check-outline",
	Analysis: "chart-bar",
};

const activityTooltipByLabel: Record<string, string> = {
	Video: "Watch the scenario video.",
	Perspectives: "Review additional perspectives on the scenario.",
	Reflect: "Consider the ethical decisions in the scenario.",
	Explore: "Explore the ethical principles and frameworks.",
	Review: "Review key takeaways before moving on.",
	Analysis: "Analyze the scenario and decision points.",
};

const getModuleLabel = (heading: string) =>
	heading.match(/Module\s+\d+/i)?.[0] ?? "Module";

const getDurationLabel = (text: string) => {
	const match = text.match(/(\d+)\s+to\s+(\d+)\s+minutes/i);
	return match ? `${match[1]} to ${match[2]} min` : "30 to 40 min";
};

const getKnowledgeCheckCount = (text: string) => {
	const match = text.match(/(\w+)-question/i);
	if (!match) {
		return "5";
	}
	return match[1] === "five" ? "5" : match[1];
};

const getActivityCount = (text: string) => {
	if (/three activities/i.test(text)) {
		return "3";
	}
	return "3";
};

const capitalizeFirstLetter = (text: string) =>
	text.length ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text;

const HeaderIcon = ({ name }: { name: IconName }) => {
	const theme = useAppTheme();
	return (
		<Box
			width={36}
			height={36}
			borderRadius={0}
			alignItems="center"
			justifyContent="center"
			backgroundColor={theme.colors.primaryContainer}
		>
			<Icon
				name={name}
				size={20}
				color={theme.colors.onPrimaryContainer}
				accessible={false}
			/>
		</Box>
	);
};

const SectionPanel = ({
	title,
	icon,
	children,
	contentPaddingHorizontal = 16,
	contentPaddingVertical = 16,
}: {
	title: string;
	icon: IconName;
	children: React.ReactNode;
	contentPaddingHorizontal?: number;
	contentPaddingVertical?: number;
}) => {
	const theme = useAppTheme();
	return (
		<Box
			borderRadius={0}
			borderWidth={1}
			borderColor={theme.colors.outlineVariant}
			backgroundColor={theme.colors.surface}
			overflow="hidden"
		>
			<Box
				flexDirection="row"
				alignItems="center"
				paddingHorizontal={18}
				paddingVertical={14}
				style={{ gap: 12 }}
			>
				<HeaderIcon name={icon} />
				<Text
					variant="titleMedium"
					style={{ color: theme.colors.onSurface, fontWeight: "800" }}
					accessibilityRole="header"
				>
					{title}
				</Text>
			</Box>
			<Box height={1} backgroundColor={theme.colors.outlineVariant} />
			<Box
				paddingHorizontal={contentPaddingHorizontal}
				paddingVertical={contentPaddingVertical}
			>
				{children}
			</Box>
		</Box>
	);
};

const HeroStat = ({
	icon,
	value,
	label,
	compact,
	first,
}: {
	icon: IconName;
	value: string;
	label: string;
	compact?: boolean;
	first?: boolean;
}) => {
	const theme = useAppTheme();
	return (
		<Box
			width="100%"
			minHeight={compact ? 54 : 58}
			borderColor="rgba(255, 255, 255, 0.44)"
			paddingHorizontal={compact ? 0 : 18}
			paddingVertical={compact ? 12 : 10}
			flexDirection="row"
			alignItems="center"
			style={{
				borderTopWidth: first ? 0 : 1,
				gap: 10,
			}}
		>
			<Icon name={icon} size={30} color={theme.colors.onPrimary} />
			<Box flex={1} style={{ gap: 2 }}>
				<Text
					variant="titleSmall"
					style={{ color: theme.colors.onPrimary, fontWeight: "800" }}
				>
					{value}
				</Text>
				<Text
					variant="labelMedium"
					style={{ color: theme.colors.onPrimary, opacity: 0.9 }}
				>
					{label}
				</Text>
			</Box>
		</Box>
	);
};

const ActivityTile = ({ label, icon }: { label: string; icon?: string }) => {
	const theme = useAppTheme();
	return (
		<TooltipWrapper
			title={activityTooltipByLabel[label] ?? label}
			style={{ display: "flex", flex: 1, alignSelf: "stretch", height: "100%" }}
		>
			<Box
				width="100%"
				height="100%"
				minHeight={72}
				borderRadius={0}
				borderWidth={1}
				borderColor="rgba(0, 0, 0, 0.08)"
				backgroundColor={theme.colors.surfaceVariant}
				alignItems="center"
				justifyContent="center"
				paddingHorizontal={10}
				paddingVertical={8}
				style={{ gap: 4, flex: 1, alignSelf: "stretch" }}
			>
				<Icon
					name={
						(icon as IconName | undefined) ??
						activityIconByLabel[label] ??
						"checkbox-marked-outline"
					}
					size={18}
					color={theme.colors.primary}
					accessible={false}
				/>
				<Text
					variant="labelMedium"
					style={{
						color: theme.colors.onSurfaceVariant,
						fontWeight: "700",
						lineHeight: 16,
						textAlign: "center",
					}}
				>
					{label}
				</Text>
			</Box>
		</TooltipWrapper>
	);
};

const OutlineSection = ({
	section,
	index,
	defaultExpanded,
}: {
	section: { title: string; items: OutlineItem[] };
	index: number;
	defaultExpanded?: boolean;
}) => {
	const [expanded, setExpanded] = useState(defaultExpanded ?? true);
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const items = section.items.map(toOutlineActivity);
	const hasItems = items.length > 0;
	const activityRows: OutlineActivity[][] = [];
	for (let i = 0; i < items.length; i += 3) {
		activityRows.push(items.slice(i, i + 3));
	}
	const leadingIcon = hasItems ? "account-outline" : "scale-balance";
	const rowContent = (
		<Box
			flexDirection="row"
			alignItems="center"
			justifyContent="space-between"
			paddingHorizontal={18}
			paddingVertical={14}
			style={{ gap: 12 }}
		>
			<Box
				width={36}
				height={36}
				borderRadius={0}
				alignItems="center"
				justifyContent="center"
				backgroundColor={theme.colors.primaryContainer}
			>
				<Icon
					name={leadingIcon}
					size={20}
					color={theme.colors.onPrimaryContainer}
					accessible={false}
				/>
			</Box>
			<Text
				variant="titleSmall"
				style={{
					color: theme.colors.onSurface,
					flex: 1,
					fontWeight: "800",
				}}
			>
				{section.title}
			</Text>
			{hasItems ? (
				<Icon
					name={expanded ? "chevron-up" : "chevron-down"}
					size={20}
					color={theme.colors.onSurfaceVariant}
					accessible={false}
				/>
			) : null}
		</Box>
	);

	return (
		<Box
			borderRadius={0}
			backgroundColor={theme.colors.surface}
			overflow="hidden"
			style={
				index === 0
					? undefined
					: { borderTopWidth: 1, borderTopColor: theme.colors.outlineVariant }
			}
		>
			{hasItems ? (
				<Pressable
					onPress={() => setExpanded((e) => !e)}
					accessibilityRole="button"
					accessibilityState={{ expanded }}
					accessibilityLabel={section.title}
				>
					{rowContent}
				</Pressable>
			) : (
				rowContent
			)}
			{hasItems && expanded ? (
				<Box
					paddingHorizontal={isMobile ? 10 : 18}
					paddingBottom={16}
					paddingLeft={isMobile ? 10 : 66}
					style={{ gap: 10, minWidth: 0 }}
				>
					{activityRows.map((row, rowIndex) => (
						<Box
							key={`${section.title}-activity-row-${rowIndex}`}
							flexDirection="row"
							alignItems="stretch"
							style={{ gap: isMobile ? 6 : 10, minWidth: 0 }}
						>
							{row.map((item) => (
								<Box
									key={item.title}
									flex={1}
									minWidth={0}
									alignSelf="stretch"
									style={{ display: "flex" }}
								>
									<ActivityTile label={item.title} icon={item.icon} />
								</Box>
							))}
							{Array.from({ length: Math.max(0, 3 - row.length) }).map(
								(_, i) => (
									<Box
										// biome-ignore lint/suspicious/noArrayIndexKey: fixed spacer slots for a three-column row.
										key={`outline-spacer-${rowIndex}-${i}`}
										flex={1}
										minWidth={0}
									/>
								),
							)}
						</Box>
					))}
				</Box>
			) : null}
		</Box>
	);
};

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: This component composes the full overview layout.
const ModuleLanding = ({ data }: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const moduleLabel =
		data.module?.label ?? getModuleLabel(data.outline.heading);
	const sessionTitle =
		data.module?.title ??
		data.outline.sections.find((section) => section.items.length === 0)
			?.title ??
		data.outline.heading;
	const durationLabel =
		data.module?.durationLabel ?? getDurationLabel(data.lesson.text);
	const activityCount = `${data.module?.activityCount ?? getActivityCount(data.lesson.text)}`;
	const knowledgeCheckCount = `${
		data.module?.knowledgeCheckQuestionCount ??
		getKnowledgeCheckCount(data.lesson.text)
	}`;

	const stemFontSize = useAppStore((store) => store.app.fontSizes?.stem);

	return (
		<ScrollView showsVerticalScrollIndicator={false}>
			<Box gap={16} paddingBottom={24}>
				<Box borderRadius={0} backgroundColor={theme.colors.primary}>
					<Box
						flexDirection={isMobile ? "column" : "row"}
						alignItems={isMobile ? "stretch" : "center"}
						paddingHorizontal={isMobile ? 20 : 28}
						paddingVertical={isMobile ? 20 : 22}
						style={{ gap: isMobile ? 18 : 28 }}
					>
						<Box
							width={isMobile ? "100%" : 132}
							height={isMobile ? 124 : 132}
							alignItems="center"
							justifyContent="center"
						>
							{data.module?.badgeSource ? (
								<Image
									source={data.module.badgeSource}
									style={{
										width: isMobile ? 124 : 132,
										height: isMobile ? 124 : 132,
									}}
									contentFit="contain"
									alt={`${moduleLabel} badge`}
								/>
							) : (
								<Icon
									name="school-outline"
									size={72}
									color={theme.colors.onPrimary}
								/>
							)}
						</Box>
						<Box
							flex={1}
							flexDirection={isMobile ? "column" : "row"}
							alignItems={isMobile ? "stretch" : "center"}
							style={{ gap: isMobile ? 18 : 24 }}
						>
							<Box flex={1} style={{ gap: 7 }}>
								<CustomMarkdownRenderer
									text={`## ${moduleLabel}`}
									fontSize={stemFontSize || 16}
									customStyles={{
										text: {
											color: theme.colors.onPrimary,
										},
										heading2: {
											marginVertical: 0,
										},
									}}
								/>
								<Text
									variant="titleMedium"
									style={{
										color: theme.colors.onPrimary,
										fontWeight: "700",
									}}
								>
									{sessionTitle}
								</Text>
								<Text
									variant="bodyMedium"
									style={{
										color: theme.colors.onPrimary,
										lineHeight: 21,
										maxWidth: 440,
									}}
								>
									{data.lesson.text}
								</Text>
							</Box>
							<Box
								width={isMobile ? "100%" : 196}
								borderColor="rgba(255, 255, 255, 0.44)"
								flexDirection="column"
								alignItems="stretch"
								style={{ borderLeftWidth: isMobile ? 0 : 1 }}
							>
								<HeroStat
									icon="clock-outline"
									value={durationLabel}
									label="Estimated time"
									compact={isMobile}
									first
								/>
								{activityCount !== "0" ? (
									<HeroStat
										icon="format-list-bulleted"
										value={activityCount}
										label="Activities"
										compact={isMobile}
									/>
								) : null}
								{knowledgeCheckCount !== "0" ? (
									<HeroStat
										icon="help-circle-outline"
										value={knowledgeCheckCount}
										label="Knowledge check"
										compact={isMobile}
									/>
								) : null}
							</Box>
						</Box>
					</Box>
				</Box>

				<Box
					flexDirection={isMobile ? "column" : "row"}
					alignItems="flex-start"
					style={{ gap: 16 }}
				>
					<Box
						flex={isMobile ? undefined : 1.18}
						width={isMobile ? "100%" : undefined}
					>
						<SectionPanel
							title="Module Outline"
							icon="format-list-numbered"
							contentPaddingHorizontal={0}
							contentPaddingVertical={0}
						>
							<Box>
								{data.outline.sections.map((section, i) => (
									<OutlineSection
										key={section.title}
										index={i}
										section={section}
										defaultExpanded={false}
									/>
								))}
							</Box>
						</SectionPanel>
					</Box>

					<Box flex={1} width={isMobile ? "100%" : undefined}>
						<SectionPanel
							title={data.learningObjectives.heading}
							icon="target"
							contentPaddingHorizontal={0}
							contentPaddingVertical={0}
						>
							<Box>
								{data.learningObjectives.items.map((item, i) => (
									<Box
										key={`${data.learningObjectives.heading}-${i}`}
										flexDirection="row"
										alignItems="flex-start"
										paddingVertical={16}
										paddingHorizontal={16}
										borderBottomWidth={
											i === data.learningObjectives.items.length - 1 ? 0 : 1
										}
										borderBottomColor={theme.colors.outlineVariant}
										style={{ gap: 14 }}
									>
										<Box
											width={32}
											height={32}
											borderRadius={0}
											backgroundColor={theme.colors.primaryContainer}
											alignItems="center"
											justifyContent="center"
											style={{ marginTop: 1 }}
										>
											<Text
												variant="labelMedium"
												style={{
													color: theme.colors.primary,
													fontWeight: "800",
												}}
											>
												{i + 1}
											</Text>
										</Box>
										<Box flex={1}>
											<MarkdownRenderer
												text={capitalizeFirstLetter(item)}
												fontSize={14}
												paragraphMarginVertical={0}
											/>
										</Box>
									</Box>
								))}
							</Box>
						</SectionPanel>
					</Box>
				</Box>
			</Box>
		</ScrollView>
	);
};

export default ModuleLanding;
